import React, { useState, useEffect } from 'react';
// import { logger } from "../../utils/logger";
import { Box, Paper, Typography, Button, Grid, FormControlLabel, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemIcon, ListItemText, Chip, Accordion, AccordionSummary, AccordionDetails, useTheme, useMediaQuery } from '@mui/material';
import { Cookie as CookieIcon, Settings as SettingsIcon, ExpandMore as ExpandMoreIcon, CheckCircle as CheckCircleIcon, Info as InfoIcon, Analytics as AnalyticsIcon, Security as SecurityIcon, Close as CloseIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { logger } from "../../utils/logger";
import { setDocumentCookieSecuredOnHttps } from "../../utils/setDocumentCookieHttps";
// Removed complex tracking prevention imports - using direct storage access for reliability
interface CookiePreferences {
    essential: boolean;
    analytics: boolean;
    marketing: boolean;
    functional: boolean;
}
interface CookieConsentBannerProps {
    onPreferencesChange?: (preferences: CookiePreferences) => void;
}

const COOKIE_CONSENT_PARENT_DOMAIN = '.americanpolicetraining.com';

/** Path, domain, SameSite, max-age / expires — Secure via setDocumentCookieSecuredOnHttps (CWE-614). */
function cookieConsentCrossSiteAttrs(opts: { maxAge?: number; expires?: string } = {}): string {
    const parts = [`path=/`, `domain=${COOKIE_CONSENT_PARENT_DOMAIN}`, 'SameSite=Lax'];
    if (opts.maxAge !== undefined) {
        parts.push(`max-age=${opts.maxAge}`);
    }
    if (opts.expires) {
        parts.push(`expires=${opts.expires}`);
    }
    return parts.join('; ');
}

/** Same path/domain as modern cookies but no SameSite — matches legacy cookies set before SameSite was added. */
function cookieConsentLegacyAttrsNoSameSite(opts: { maxAge?: number; expires?: string } = {}): string {
    const parts = [`path=/`, `domain=${COOKIE_CONSENT_PARENT_DOMAIN}`];
    if (opts.maxAge !== undefined) {
        parts.push(`max-age=${opts.maxAge}`);
    }
    if (opts.expires) {
        parts.push(`expires=${opts.expires}`);
    }
    return parts.join('; ');
}

/** Single write path for consent cookies; Secure on HTTPS (CWE-614). */
function applyConsentCookieLine(body: string, attrs: string): void {
    const cookie = `${body}; ${attrs}`;
    setDocumentCookieSecuredOnHttps(cookie);
}

function setCrossSiteConsentCookie(body: string, opts: { maxAge?: number; expires?: string } = {}): void {
    applyConsentCookieLine(body, cookieConsentCrossSiteAttrs(opts));
}

const COOKIE_EXPIRE_PAST = 'Thu, 01 Jan 1970 00:00:00 GMT';

/**
 * Delete a consent cookie by expiring it. Writes both SameSite=Lax and legacy (no SameSite) variants so
 * browsers that key cookie identity on SameSite still remove older cookies from prior deployments.
 */
function deleteCrossSiteConsentCookie(cookieName: string): void {
    const body = `${cookieName}=`;
    const expireOpts = { expires: COOKIE_EXPIRE_PAST };
    applyConsentCookieLine(body, cookieConsentCrossSiteAttrs(expireOpts));
    applyConsentCookieLine(body, cookieConsentLegacyAttrsNoSameSite(expireOpts));
}

const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({ onPreferencesChange }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [showBanner, setShowBanner] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [preferences, setPreferences] = useState<CookiePreferences>({
        essential: true, // Always true, cannot be disabled
        analytics: true, // Default to true for better user experience
        marketing: true, // Default to true for better user experience
        functional: true // Default to true for better user experience
    });
    useEffect(() => {
        // Enhanced consent check - prioritize cookies for cross-subdomain persistence
        const checkConsent = () => {
            try {
                // FIRST: Check cookies (works across subdomains)
                // This is the most reliable method for cross-subdomain persistence
                try {
                    const cookieString = document.cookie;
                    const cookieKeys = ['cookieConsent', 'apt_cookieConsent', 'cookie_consent'];
                    for (const key of cookieKeys) {
                        const value = `; ${cookieString}`;
                        const parts = value.split(`; ${key}=`);
                        if (parts.length === 2) {
                            const consent = parts.pop()?.split(';').shift()?.trim() || null;
                            if (consent === 'accepted' || consent === 'true') {
                                return 'accepted';
                            }
                        }
                    }
                }
                catch (error) {
                    // Continue to other methods if cookie check fails
                }
                // SECOND: Check localStorage (domain-specific, fallback for same subdomain)
                const storageKeys = ['cookieConsent', 'apt_cookieConsent', 'cookie_consent'];
                for (const key of storageKeys) {
                    try {
                        const consent = localStorage.getItem(key);
                        if (consent === 'accepted' || consent === 'true') {
                            return 'accepted';
                        }
                    }
                    catch (e) {
                        // Continue to next key
                    }
                }
                // THIRD: Check sessionStorage (domain-specific, fallback)
                for (const key of storageKeys) {
                    try {
                        const consent = sessionStorage.getItem(key);
                        if (consent === 'accepted' || consent === 'true') {
                            return 'accepted';
                        }
                    }
                    catch (e) {
                        // Continue to next key
                    }
                }
                return null;
            }
            catch (error) {
                // If all storage methods fail, assume no consent
                return null;
            }
        };
        // Helper function to get cookie value
        const getCookie = (name: string): string | null => {
            try {
                const value = `; ${document.cookie}`;
                const parts = value.split(`; ${name}=`);
                if (parts.length === 2) {
                    return parts.pop()?.split(';').shift()?.trim() || null;
                }
            }
            catch (error) {
                // Ignore cookie errors
            }
            return null;
        };
        const consentGiven = checkConsent();
        // Debug logging in development
        if (process.env.NODE_ENV === 'development') {
            logger.security('[Cookie Consent] Consent check result:', { "arg0": {
                    consentGiven,
                    cookies: {
                        cookieConsent: getCookie('cookieConsent'),
                        apt_cookieConsent: getCookie('apt_cookieConsent'),
                        cookie_consent: getCookie('cookie_consent'),
                        cookiePreferences: getCookie('cookiePreferences'),
                        allCookies: document.cookie
                    },
                    localStorage: {
                        cookieConsent: localStorage.getItem('cookieConsent'),
                        apt_cookieConsent: localStorage.getItem('apt_cookieConsent'),
                        cookie_consent: localStorage.getItem('cookie_consent'),
                        cookiePreferences: localStorage.getItem('cookiePreferences'),
                        hasVisitedBefore: localStorage.getItem('hasVisitedBefore')
                    },
                    sessionStorage: {
                        cookieConsent: sessionStorage.getItem('cookieConsent'),
                        apt_cookieConsent: sessionStorage.getItem('apt_cookieConsent'),
                        cookie_consent: sessionStorage.getItem('cookie_consent'),
                        cookiePreferences: sessionStorage.getItem('cookiePreferences'),
                        hasVisitedBefore: sessionStorage.getItem('hasVisitedBefore')
                    }
                } });
        }
        if (consentGiven === 'accepted') {
            // Consent has been given - load saved preferences and DON'T show banner
            try {
                // FIRST: Try to load preferences from cookies (works across subdomains)
                let savedPreferences: CookiePreferences | null = null;
                const cookiePrefs = getCookie('cookiePreferences');
                if (cookiePrefs) {
                    try {
                        savedPreferences = JSON.parse(decodeURIComponent(cookiePrefs));
                    }
                    catch (e) {
                        // Try parsing without decodeURIComponent as fallback
                        try {
                            savedPreferences = JSON.parse(cookiePrefs);
                        }
                        catch (e2) {
                            logger.security('Failed to parse cookiePreferences from cookie:', { "arg0": e2 });
                        }
                    }
                }
                // SECOND: Fallback to localStorage (domain-specific)
                if (!savedPreferences) {
                    const localPrefs = localStorage.getItem('cookiePreferences');
                    if (localPrefs) {
                        savedPreferences = JSON.parse(localPrefs);
                    }
                }
                // THIRD: Fallback to sessionStorage (domain-specific)
                if (!savedPreferences) {
                    const sessionPrefs = sessionStorage.getItem('cookiePreferences');
                    if (sessionPrefs) {
                        savedPreferences = JSON.parse(sessionPrefs);
                    }
                }
                if (savedPreferences) {
                    setPreferences(prev => ({ ...prev, ...savedPreferences }));
                    applyCookiePreferences(savedPreferences);
                }
                else {
                    // Apply default preferences if no saved preferences found
                    const defaultPreferences = {
                        essential: true,
                        analytics: true,
                        marketing: true,
                        functional: true
                    };
                    setPreferences(defaultPreferences);
                    applyCookiePreferences(defaultPreferences);
                }
                // Explicitly hide banner since consent was given
                setShowBanner(false);
            }
            catch (error) {
                logger.security('Error loading cookie preferences:', { "arg0": error });
                // Apply default preferences on error
                const defaultPreferences = {
                    essential: true,
                    analytics: true,
                    marketing: true,
                    functional: true
                };
                setPreferences(defaultPreferences);
                applyCookiePreferences(defaultPreferences);
                setShowBanner(false);
            }
        }
        else {
            // No explicit consent found - show banner to get user consent
            // This ensures GDPR compliance by requiring explicit consent
            setShowBanner(true);
            // Mark as visited for tracking purposes (not for auto-consent)
            try {
                localStorage.setItem('hasVisitedBefore', 'true');
                sessionStorage.setItem('hasVisitedBefore', 'true');
                setCrossSiteConsentCookie('hasVisitedBefore=true', { maxAge: 31536000 });
            }
            catch (error) {
                // Ignore storage errors
            }
        }
        // Listen for cookie settings link click from footer
        const handleOpenCookieSettings = () => {
            setShowSettings(true);
        };
        window.addEventListener('openCookieSettings', handleOpenCookieSettings);
        return () => {
            window.removeEventListener('openCookieSettings', handleOpenCookieSettings);
        };
    }, []);
    const handleAcceptAll = () => {
        const allPreferences = {
            essential: true,
            analytics: true,
            marketing: true,
            functional: true
        };
        savePreferences(allPreferences);
    };
    const handleRejectAll = () => {
        const newPreferences: CookiePreferences = {
            essential: true,
            analytics: false,
            marketing: false,
            functional: false
        };
        setPreferences(newPreferences);
        setShowBanner(false);
        // Save consent and preferences to multiple storage methods for reliability
        saveConsentToMultipleStorages(newPreferences);
        // Apply preferences to analytics and tracking immediately
        applyCookiePreferences(newPreferences);
        if (onPreferencesChange) {
            onPreferencesChange(newPreferences);
        }
    };
    const handleSavePreferences = () => {
        savePreferences(preferences);
        setShowSettings(false);
    };
    const savePreferences = (newPreferences: CookiePreferences) => {
        // Save to multiple storage methods for reliability
        saveConsentToMultipleStorages(newPreferences);
        setPreferences(newPreferences);
        setShowBanner(false);
        onPreferencesChange?.(newPreferences);
        // Apply preferences to analytics and tracking
        applyCookiePreferences(newPreferences);
    };
    const saveConsentToMultipleStorages = (prefs: CookiePreferences) => {
        const consentData = 'accepted'; // Use 'accepted' instead of 'true' for consistency
        const preferencesData = JSON.stringify(prefs);
        const consentDate = new Date().toISOString();
        // Save to localStorage (primary method)
        try {
            localStorage.setItem('cookieConsent', consentData);
            localStorage.setItem('cookiePreferences', preferencesData);
            localStorage.setItem('cookieConsentDate', consentDate);
            // Also save with alternative keys for better compatibility
            localStorage.setItem('apt_cookieConsent', consentData);
            localStorage.setItem('cookie_consent', consentData);
        }
        catch (error) {
            logger.error('Failed to save to localStorage:', error, {});
        }
        // Save to sessionStorage as backup
        try {
            sessionStorage.setItem('cookieConsent', consentData);
            sessionStorage.setItem('cookiePreferences', preferencesData);
            sessionStorage.setItem('apt_cookieConsent', consentData);
            sessionStorage.setItem('cookie_consent', consentData);
        }
        catch (error) {
            logger.error('Failed to save to sessionStorage:', error, {});
        }
        // Save to cookies as additional backup
        try {
            setCrossSiteConsentCookie(`cookieConsent=${consentData}`, { maxAge: 31536000 });
            setCrossSiteConsentCookie(`cookiePreferences=${encodeURIComponent(preferencesData)}`, { maxAge: 31536000 });
            setCrossSiteConsentCookie(`apt_cookieConsent=${consentData}`, { maxAge: 31536000 });
            setCrossSiteConsentCookie(`cookie_consent=${consentData}`, { maxAge: 31536000 });
        }
        catch (error) {
            logger.error('Failed to save to cookies:', error, {});
        }
        // Debug logging in development
        if (process.env.NODE_ENV === 'development') {
            logger.security('[Cookie Consent] Saved consent data:', { "arg0": {
                    preferences: prefs,
                    timestamp: consentDate,
                    storageKeys: ['cookieConsent', 'apt_cookieConsent', 'cookie_consent']
                } });
        }
    };
    // Function to clear all consent data (useful for testing or resetting)
    const clearAllConsentData = () => {
        // Clear localStorage
        try {
            localStorage.removeItem('cookieConsent');
            localStorage.removeItem('cookiePreferences');
            localStorage.removeItem('cookieConsentDate');
            localStorage.removeItem('apt_cookieConsent');
            localStorage.removeItem('cookie_consent');
            localStorage.removeItem('hasVisitedBefore');
        }
        catch (error) {
            logger.error('Failed to clear localStorage:', error, {});
        }
        // Clear sessionStorage
        try {
            sessionStorage.removeItem('cookieConsent');
            sessionStorage.removeItem('cookiePreferences');
            sessionStorage.removeItem('apt_cookieConsent');
            sessionStorage.removeItem('cookie_consent');
            sessionStorage.removeItem('hasVisitedBefore');
        }
        catch (error) {
            logger.error('Failed to clear sessionStorage:', error, {});
        }
        // Clear cookies (dual delete: SameSite=Lax + legacy without SameSite — see deleteCrossSiteConsentCookie)
        try {
            deleteCrossSiteConsentCookie('cookieConsent');
            deleteCrossSiteConsentCookie('cookiePreferences');
            deleteCrossSiteConsentCookie('apt_cookieConsent');
            deleteCrossSiteConsentCookie('cookie_consent');
            deleteCrossSiteConsentCookie('hasVisitedBefore');
        }
        catch (error) {
            logger.error('Failed to clear cookies:', error, {});
        }
        // Reset state
        setShowBanner(false);
        setShowSettings(false);
        if (process.env.NODE_ENV === 'development') {
            logger.security('[Cookie Consent] Cleared all consent data');
        }
    };
    // Expose clear function to window for debugging
    if (process.env.NODE_ENV === 'development') {
        (window as any).clearCookieConsent = clearAllConsentData;
    }
    const applyCookiePreferences = (prefs: CookiePreferences) => {
        // Push consent update to dataLayer for GTM
        if (window.dataLayer) {
            window.dataLayer.push({
                event: 'consent_update',
                consent_state: {
                    analytics_storage: prefs.analytics ? 'granted' : 'denied',
                    ad_storage: prefs.marketing ? 'granted' : 'denied',
                    ad_user_data: prefs.marketing ? 'granted' : 'denied',
                    ad_personalization: prefs.marketing ? 'granted' : 'denied',
                    functionality_storage: prefs.functional ? 'granted' : 'denied',
                    personalization_storage: prefs.functional ? 'granted' : 'denied',
                    security_storage: 'granted'
                }
            });
        }
        // Also update gtag for backward compatibility (during migration)
        if (window.gtag) {
            window.gtag('consent', 'update', {
                // Analytics storage
                'analytics_storage': prefs.analytics ? 'granted' : 'denied',
                // Ad-related storage (Consent Mode v2 required parameters)
                'ad_storage': prefs.marketing ? 'granted' : 'denied',
                'ad_user_data': prefs.marketing ? 'granted' : 'denied',
                'ad_personalization': prefs.marketing ? 'granted' : 'denied',
                // Functional storage
                'functionality_storage': prefs.functional ? 'granted' : 'denied',
                'personalization_storage': prefs.functional ? 'granted' : 'denied',
                // Security storage is always granted (required for site functionality)
                'security_storage': 'granted'
            });
            // Log consent update for debugging in development
            if (process.env.NODE_ENV === 'development') {
                logger.debug('🍪 Consent Mode v2 Updated (gtag + GTM dataLayer):', { "arg0": {
                        analytics: prefs.analytics ? 'granted' : 'denied',
                        marketing: prefs.marketing ? 'granted' : 'denied',
                        functional: prefs.functional ? 'granted' : 'denied'
                    } });
            }
        }
        // Apply functional preferences via cookies
        if (prefs.functional) {
            // Enable functional cookies (preferences, etc.)
            try {
                setCrossSiteConsentCookie('functional_cookies=enabled', { maxAge: 31536000 });
            }
            catch (error) {
                logger.error('Failed to set functional cookies:', error, {});
            }
        }
        else {
            // Disable functional cookies
            try {
                setCrossSiteConsentCookie('functional_cookies=disabled', { maxAge: 31536000 });
            }
            catch (error) {
                logger.error('Failed to set functional cookies:', error, {});
            }
        }
    };
    const handlePreferenceChange = (category: keyof CookiePreferences) => (event: React.ChangeEvent<HTMLInputElement>) => {
        if (category === 'essential')
            return; // Essential cookies cannot be disabled
        setPreferences(prev => ({
            ...prev,
            [category]: event.target.checked
        }));
    };
    const cookieCategories = [
        {
            key: 'essential' as const,
            title: 'Essential Cookies',
            description: 'These cookies are necessary for the website to function properly. They cannot be disabled.',
            icon: <SecurityIcon color="primary"/>,
            required: true
        },
        {
            key: 'analytics' as const,
            title: 'Analytics Cookies',
            description: 'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
            icon: <AnalyticsIcon color="primary"/>,
            required: false
        },
        {
            key: 'marketing' as const,
            title: 'Marketing Cookies',
            description: 'These cookies support advertising measurement and relevance, including Google AdSense where ads are shown. This may involve sharing for cross-context behavioral advertising as described in our Privacy Policy. Opt out via these Cookie Settings or our Data Request page (/data-request).',
            icon: <CookieIcon color="primary"/>,
            required: false
        },
        {
            key: 'functional' as const,
            title: 'Functional Cookies',
            description: 'These cookies enable enhanced functionality and personalization, such as remembering your preferences.',
            icon: <SettingsIcon color="primary"/>,
            required: false
        }
    ];
    if (!showBanner && !showSettings) {
        return null;
    }
    return (<>
      {/* Main Cookie Banner */}
      {showBanner && (<Box sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 9999,
                // Avoid expensive backdrop blur competing with LCP/TBT on mobile
                backgroundColor: { xs: 'transparent', md: 'rgba(0, 0, 0, 0.45)' },
            }}>
          <Paper sx={{
                m: { xs: 1, md: 2 },
                p: { xs: 2, md: 3 },
                maxWidth: '1200px',
                mx: 'auto',
                borderRadius: 2,
                boxShadow: { xs: 2, md: 3 },
            }}>
            <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <CookieIcon sx={{ mr: 1, color: 'primary.main' }}/>
                  <Typography variant="h6" component="h2">
                    Cookie Preferences
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 0, md: 2 } }}>
                  We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                  Analytics and functional cookies are enabled by default to improve your experience. 
                  You can customize or reject non-essential cookies at any time.
                  <Link to="/cookie-policy" style={{ color: theme.palette.primary.dark, textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                    {' '}Learn more about our Cookie Policy
                  </Link>
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button variant="contained" color="primary" onClick={handleAcceptAll} fullWidth size="large">
                    Accept All
                  </Button>
                  <Button variant="outlined" onClick={() => setShowSettings(true)} fullWidth size="large">
                    Customize
                  </Button>
                  <Button variant="text" onClick={handleRejectAll} fullWidth size="small" sx={{ color: 'text.secondary' }}>
                    Reject Non-Essential
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>)}

      {/* Cookie Settings Dialog */}
      <Dialog open={showSettings} onClose={() => setShowSettings(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SettingsIcon sx={{ mr: 1, color: 'primary.main' }}/>
              <Typography variant="h6">Cookie Settings</Typography>
            </Box>
            <Button onClick={() => setShowSettings(false)} startIcon={<CloseIcon />} color="inherit">
              Close
            </Button>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manage your cookie preferences below. Essential cookies are always enabled as they are necessary for the website to function properly. 
            Analytics and functional cookies are enabled by default, but you can disable them if you prefer.
          </Typography>

          <List>
            {cookieCategories.map((category) => (<ListItem key={category.key} sx={{ flexDirection: 'column', alignItems: 'flex-start', p: 0, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {category.icon}
                  </ListItemIcon>
                  <ListItemText primary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6">{category.title}</Typography>
                        {category.required && (<Chip label="Required" size="small" color="primary" variant="outlined"/>)}
                      </Box>}/>
                  <FormControlLabel control={<Checkbox checked={preferences[category.key]} onChange={handlePreferenceChange(category.key)} disabled={category.required}/>} label=""/>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
                  {category.description}
                </Typography>
              </ListItem>))}
          </List>

          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> You can change these settings at any time by clicking the "Cookie Settings" link in our footer. 
              For more information about how we use cookies, please see our{' '}
              <Link to="/cookie-policy" style={{ color: theme.palette.primary.dark, textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                Cookie Policy
              </Link>
              {', '}
              <Link to="/privacy" style={{ color: theme.palette.primary.dark, textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                Privacy Policy
              </Link>
              {', or submit a '}
              <Link to="/data-request" style={{ color: theme.palette.primary.dark, textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                Data Request
              </Link>
              {' '}(including opt-out of sale/share).
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setShowSettings(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSavePreferences} variant="contained" color="primary" startIcon={<CheckCircleIcon />}>
            Save Preferences
          </Button>
          {process.env.NODE_ENV === 'development' && (<Button onClick={clearAllConsentData} variant="outlined" color="error" size="small" sx={{ ml: 'auto' }}>
              Reset Consent (Dev)
            </Button>)}
        </DialogActions>
        
        {/* Development-only debug info */}
        {process.env.NODE_ENV === 'development' && (<Box sx={{ p: 2, bgcolor: 'grey.100', borderTop: 1, borderColor: 'grey.300' }}>
            <Typography variant="caption" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
              Debug Info (Development Only):
            </Typography>
            <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
              Banner Visible: {showBanner.toString()}<br />
              Settings Open: {showSettings.toString()}<br />
              Current Preferences: {JSON.stringify(preferences, null, 2)}
            </Typography>
          </Box>)}
      </Dialog>
    </>);
};
export default CookieConsentBanner;
